import { useState } from "react";
import {
  MIN_POOLED_COMMENTS,
  type CommentSummaryContent,
  type CommentSummaryDto,
  type ReportAudience,
  type ReportDto,
} from "@genoma/shared";

import { useI18n } from "../../i18n/index.tsx";
import {
  generateSummary,
  reviseSummary,
} from "../../services/report.service.ts";
import { useCan } from "../../stores/auth.store.ts";

type Props = {
  participantId: string;
  feedback: ReportDto["openFeedback"];
  audience: ReportAudience;
  onChange: (summary: CommentSummaryDto) => void;
};

/** The three list sections. `essence` is a paragraph and behaves differently. */
type ThemeSection = "strengths" | "growth" | "divergences";
type Section = ThemeSection | "essence";

/**
 * What the API will accept, kept here so the interface refuses before the
 * server does. A save rejected after the fact loses whatever was typed.
 */
const LIMITS = {
  essence: { min: 40, max: 1500 },
  theme: { min: 2, max: 80 },
  detail: { min: 10, max: 900 },
  count: { strengths: 4, growth: 4, divergences: 3 },
} as const;

/**
 * What people wrote, as a coach would say it back.
 *
 * The comments themselves are not here, and for the leader they are not in the
 * payload either. A sentence carries the idiom, the example and the timing that
 * identify whoever wrote it; the synthesis carries what they meant. That trade
 * is the entire reason this component exists, so it is stated on the page
 * rather than left for the reader to infer.
 *
 * For the coach it is a working surface: generate, read against the sources,
 * approve. Nothing reaches the leader until somebody has done that — a machine
 * draft of the most sensitive page in the report is not something to publish
 * unread.
 *
 * Rewriting the text is a further step and an admin's alone, under
 * `summary:edit`. Reviewing is judging a synthesis against the sentences it
 * came from; editing is changing what the leader will read, and the two are
 * not the same authority. An admin edits per section, and the whole content
 * goes back on every save because the API takes the object whole.
 */
const Theme = ({
  theme,
  detail,
  accent,
}: {
  theme: string;
  detail: string;
  accent: "strength" | "growth" | "divergence";
}) => (
  <li className="border-t border-line-soft py-3 first:border-t-0">
    <p
      className={[
        "text-[14px] font-bold",
        accent === "strength"
          ? "text-teal-deep"
          : accent === "growth"
            ? "text-ink"
            : "text-soft",
      ].join(" ")}
    >
      {theme}
    </p>
    <p className="mt-1 text-[14.5px] leading-relaxed text-soft">{detail}</p>
  </li>
);

/** The small control beside a heading. Only an admin ever sees one. */
const EditButton = ({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="cursor-pointer rounded-full border border-line bg-paper px-2.5 py-1 font-mono text-[9.5px] tracking-[0.12em] text-soft uppercase transition hover:border-teal hover:text-teal print:hidden"
  >
    {label}
  </button>
);

export const CommentSummary = ({
  participantId,
  feedback,
  audience,
  onChange,
}: Props) => {
  const reviewing = audience === "REVIEWER";
  /**
   * Drafting this text, rewriting it and signing it off are an admin's — see
   * the `summary:manage` permission. The API refuses either way; this is so a
   * coach is not offered buttons that would only tell them no.
   */
  const mayManage = useCan("summary:manage");
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSources, setShowSources] = useState(false);

  /**
   * Which section is open, and the copy being typed into.
   *
   * One at a time: two open editors mean two drafts of the same object, and
   * whichever saved last would silently throw the other away.
   */
  const [editing, setEditing] = useState<Section | null>(null);
  const [draft, setDraft] = useState<CommentSummaryContent | null>(null);

  const summary = feedback.summary;
  /**
   * Below the minimum there is nothing to synthesise: a summary of two comments
   * is a paraphrase of two comments, and the leader can still work out who is
   * being described. The API refuses it too — this only keeps the button from
   * offering something that cannot happen.
   */
  const summarizable = feedback.writtenCount > 0 && feedback.withheld === 0;

  const run = async (action: () => Promise<CommentSummaryDto>) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await action());
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("errors.generic"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openEditor = (section: Section) => {
    if (!summary) return;
    setError(null);
    // A copy, not the live object: cancelling has to leave nothing behind.
    setDraft(structuredClone(summary.content));
    setEditing(section);
  };

  const closeEditor = () => {
    setEditing(null);
    setDraft(null);
    setError(null);
  };

  /**
   * What the API would refuse, said here instead.
   *
   * The last strength is the one that matters: `isSummaryUsable` rejects a
   * summary without one, so removing it would fail on save with the rest of
   * the edits already typed.
   */
  const complain = (content: CommentSummaryContent): string | null => {
    if (content.essence.trim().length < LIMITS.essence.min)
      return t("summary.essenceTooShort");
    if (content.strengths.length === 0) return t("summary.lastStrength");
    for (const list of [
      content.strengths,
      content.growth,
      content.divergences,
    ]) {
      for (const entry of list) {
        if (entry.theme.trim().length < LIMITS.theme.min)
          return t("summary.tooLong");
        if (entry.detail.trim().length < LIMITS.detail.min)
          return t("summary.tooLong");
        if (entry.theme.length > LIMITS.theme.max) return t("summary.tooLong");
        if (entry.detail.length > LIMITS.detail.max)
          return t("summary.tooLong");
      }
    }
    return null;
  };

  const saveDraft = async () => {
    if (!draft) return;
    const complaint = complain(draft);
    if (complaint) {
      setError(complaint);
      return;
    }
    const ok = await run(() =>
      reviseSummary(participantId, { content: draft }),
    );
    if (ok) closeEditor();
  };

  const editList = (
    section: ThemeSection,
    next: CommentSummaryContent[ThemeSection],
  ) => {
    if (!draft) return;
    setDraft({ ...draft, [section]: next });
  };

  /** The buttons under an open editor. The same pair everywhere. */
  const editorActions = (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => void saveDraft()}
        className="cursor-pointer rounded-xl bg-teal px-3.5 py-2 text-[13px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:cursor-default disabled:opacity-40"
      >
        {busy ? t("common.saving") : t("common.save")}
      </button>
      <button
        type="button"
        onClick={closeEditor}
        className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase hover:text-teal"
      >
        {t("common.cancel")}
      </button>
      {/* Said where the decision is made, not in a toast afterwards. */}
      <span className="text-[12.5px] text-faint">
        {t("summary.editUnapproves")}
      </span>
    </div>
  );

  /** One theme, open for editing: a title, its text, and a way to drop it. */
  const themeEditor = (
    section: ThemeSection,
    entry: { theme: string; detail: string },
    index: number,
    list: CommentSummaryContent[ThemeSection],
  ) => (
    <li key={index} className="border-t border-line-soft py-3 first:border-t-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <label className="block font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase">
            {t("summary.themeLabel")}
          </label>
          <input
            value={entry.theme}
            maxLength={LIMITS.theme.max}
            onChange={(event) =>
              editList(
                section,
                list.map((row, i) =>
                  i === index ? { ...row, theme: event.target.value } : row,
                ),
              )
            }
            className="mt-1 w-full rounded-lg border border-line bg-paper px-2.5 py-1.5 text-[14px] font-bold outline-none focus:border-teal"
          />
        </div>
        <button
          type="button"
          onClick={() =>
            editList(
              section,
              list.filter((_, i) => i !== index),
            )
          }
          // The last strength cannot go: the API will not store a summary
          // without one, and a disabled button with a reason beats a save
          // that fails after the fact.
          disabled={section === "strengths" && list.length === 1}
          title={
            section === "strengths" && list.length === 1
              ? t("summary.lastStrength")
              : undefined
          }
          className="mt-5 shrink-0 cursor-pointer rounded-lg border border-line bg-paper px-2.5 py-1.5 font-mono text-[9.5px] tracking-[0.12em] text-soft uppercase transition hover:border-[var(--color-critical)] hover:text-[var(--color-critical-ink)] disabled:cursor-default disabled:opacity-35 disabled:hover:border-line disabled:hover:text-soft"
        >
          {t("common.remove")}
        </button>
      </div>
      <label className="mt-2 block font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase">
        {t("summary.detailLabel")}
      </label>
      <textarea
        value={entry.detail}
        rows={3}
        maxLength={LIMITS.detail.max}
        onChange={(event) =>
          editList(
            section,
            list.map((row, i) =>
              i === index ? { ...row, detail: event.target.value } : row,
            ),
          )
        }
        className="mt-1 w-full resize-y rounded-lg border border-line bg-paper px-2.5 py-2 text-[14.5px] leading-relaxed outline-none focus:border-teal"
      />
    </li>
  );

  /** A whole list section, open for editing. */
  const listEditor = (section: ThemeSection, tone: "paper" | "sand") => {
    const list = draft?.[section] ?? [];
    return (
      <>
        <ul
          className={[
            "mt-1.5 list-none rounded-2xl border border-line px-4 py-2",
            tone === "sand" ? "bg-sand" : "bg-paper shadow-card",
          ].join(" ")}
        >
          {list.length === 0 && (
            <li className="py-3 text-[13px] text-faint">
              {t("summary.emptySection")}
            </li>
          )}
          {list.map((entry, index) => themeEditor(section, entry, index, list))}
        </ul>
        {list.length < LIMITS.count[section] && (
          <button
            type="button"
            onClick={() =>
              editList(section, [...list, { theme: "", detail: "" }])
            }
            className="mt-2 cursor-pointer rounded-xl border border-dashed border-line bg-transparent px-3 py-1.5 font-mono text-[10px] tracking-[0.1em] text-soft uppercase transition hover:border-teal hover:text-teal"
          >
            + {t("summary.addTheme")}
          </button>
        )}
        {editorActions}
      </>
    );
  };

  /** Heading plus, for a reviewer, the control that opens it. */
  const sectionHeading = (section: ThemeSection, label: string) => (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-[16px] font-bold">{label}</h3>
      {mayManage && editing === null && (
        <EditButton
          onClick={() => openEditor(section)}
          label={t("common.edit")}
        />
      )}
    </div>
  );

  /** The coach's toolbar. Never rendered for the leader. */
  const review = reviewing && (
    <div className="mt-4 rounded-2xl border border-dashed border-line bg-paper/60 px-4 py-3.5 print:hidden">
      <p className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
        {t("summary.coachOnly")}
      </p>

      {summary && (
        <p className="mt-2 text-[13px] text-soft">
          {t("summary.provenance", {
            model: summary.model,
            count: summary.sourceCount,
          })}
          {summary.editedByCoach && ` · ${t("summary.edited")}`}
          {summary.stale && ` · ${t("summary.stale")}`}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        {mayManage && (
          <button
            type="button"
            disabled={busy || summarizable === false || editing !== null}
            onClick={() =>
              void run(() =>
                generateSummary(participantId, summary?.editedByCoach === true),
              )
            }
            className="cursor-pointer rounded-xl border border-line bg-paper px-3.5 py-2 text-[13px] font-bold transition hover:border-teal disabled:cursor-default disabled:opacity-40"
          >
            {busy
              ? t("summary.working")
              : summary
                ? t("summary.regenerate")
                : t("summary.generate")}
          </button>
        )}

        {mayManage &&
          summary &&
          summary.status !== "APPROVED" &&
          editing === null && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => reviseSummary(participantId, { approve: true }))
              }
              className="cursor-pointer rounded-xl bg-teal px-3.5 py-2 text-[13px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:cursor-default disabled:opacity-40"
            >
              {t("summary.approve")}
            </button>
          )}

        {feedback.verbatimForReview.length > 0 && (
          <button
            type="button"
            onClick={() => setShowSources((open) => !open)}
            className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase hover:text-teal"
          >
            {showSources
              ? t("summary.hideSources")
              : t("summary.showSources", {
                  count: feedback.verbatimForReview.length,
                })}
          </button>
        )}
      </div>

      {error && <p className="mt-2.5 text-[13px] text-red-700">{error}</p>}

      {/* The sentences, for the one reader who has to check the synthesis
          against them. A synthesis nobody can verify is not reviewable. */}
      {showSources && (
        <div className="mt-3 border-t border-line-soft pt-3">
          <p className="text-[12.5px] text-faint">
            {t("summary.sourcesNotice")}
          </p>
          <div className="mt-2.5 flex flex-col gap-2">
            {feedback.verbatimForReview.map((text, index) => (
              <blockquote
                key={index}
                className="rounded-xl border border-line bg-sand px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-line text-soft"
              >
                {text}
              </blockquote>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!summary) {
    return (
      <>
        <div className="mt-4 rounded-2xl border border-dashed border-line bg-paper/60 px-5 py-4">
          <p className="text-[14px] text-soft">
            {/* P&O are told nothing about who wrote or how many, so for them
                the only honest line is that there is no reading yet. */}
            {audience === "HR"
              ? t("summary.notGeneratedHR")
              : feedback.writtenCount === 0
                ? t("summary.noneWritten")
                : feedback.withheld > 0
                  ? // Somebody wrote, and it is being withheld. Saying so is not
                    // the same as saying nobody did.
                    t("summary.withheld", {
                      count: feedback.withheld,
                      min: MIN_POOLED_COMMENTS,
                    })
                  : reviewing
                    ? t("summary.notGeneratedCoach")
                    : t("summary.notGenerated")}
          </p>
        </div>
        {review}
      </>
    );
  }

  // While an editor is open the draft is what the coach is looking at; the
  // saved content is what everyone else sees.
  const content = editing !== null && draft ? draft : summary.content;

  return (
    <>
      {/* Status is only meaningful to the coach; the leader never sees a
          summary that is not approved, so the badge would say nothing. */}
      {reviewing && (
        <span
          className={[
            "mt-4 inline-flex rounded-full border px-2.5 py-1 font-mono text-[9.5px] tracking-[0.12em] uppercase print:hidden",
            summary.status === "APPROVED"
              ? "border-tint-line bg-tint text-teal-deep"
              : "border-[#E3C88A] bg-[#FDF6E6] text-[#8A6A1F]",
          ].join(" ")}
        >
          {t(`summary.status.${summary.status}`)}
        </span>
      )}

      {/* The opening paragraph. One piece of prose, so it edits as one. */}
      <div className="mt-3 break-inside-avoid rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
        {editing === "essence" && draft ? (
          <>
            <textarea
              value={draft.essence}
              rows={6}
              maxLength={LIMITS.essence.max}
              onChange={(event) =>
                setDraft({ ...draft, essence: event.target.value })
              }
              className="w-full resize-y rounded-lg border border-line bg-paper px-3 py-2.5 text-[15.5px] leading-relaxed outline-none focus:border-teal"
            />
            {editorActions}
          </>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <p className="text-[15.5px] leading-relaxed">{content.essence}</p>
            {mayManage && editing === null && (
              <EditButton
                onClick={() => openEditor("essence")}
                label={t("common.edit")}
              />
            )}
          </div>
        )}
      </div>

      {(content.strengths.length > 0 || editing === "strengths") && (
        <div className="mt-5">
          {sectionHeading("strengths", t("summary.strengths"))}
          {editing === "strengths" ? (
            listEditor("strengths", "paper")
          ) : (
            <ul className="mt-1.5 list-none rounded-2xl border border-line bg-paper px-4 py-2 shadow-card [&>li]:break-inside-avoid">
              {content.strengths.map((entry) => (
                <Theme key={entry.theme} {...entry} accent="strength" />
              ))}
            </ul>
          )}
        </div>
      )}

      {(content.growth.length > 0 || editing === "growth") && (
        <div className="mt-5">
          {sectionHeading("growth", t("summary.growth"))}
          {editing === "growth" ? (
            listEditor("growth", "paper")
          ) : (
            <ul className="mt-1.5 list-none rounded-2xl border border-line bg-paper px-4 py-2 shadow-card [&>li]:break-inside-avoid">
              {content.growth.map((entry) => (
                <Theme key={entry.theme} {...entry} accent="growth" />
              ))}
            </ul>
          )}
        </div>
      )}

      {(content.divergences.length > 0 || editing === "divergences") && (
        <div className="mt-5">
          {sectionHeading("divergences", t("summary.divergences"))}
          <p className="text-[13px] text-soft">
            {t("summary.divergencesLead")}
          </p>
          {editing === "divergences" ? (
            listEditor("divergences", "sand")
          ) : (
            <ul className="mt-1.5 list-none rounded-2xl border border-line bg-sand px-4 py-2 [&>li]:break-inside-avoid">
              {content.divergences.map((entry) => (
                <Theme key={entry.theme} {...entry} accent="divergence" />
              ))}
            </ul>
          )}
        </div>
      )}

      {/*
       * An empty section has no heading to hang an edit button on, so a coach
       * who wants to add the first growth theme has nothing to click. These
       * appear only while nothing else is open, and only for a reviewer.
       */}
      {mayManage && editing === null && (
        <div className="mt-4 flex flex-wrap gap-2 print:hidden">
          {content.growth.length === 0 && (
            <EditButton
              onClick={() => openEditor("growth")}
              label={`+ ${t("summary.growth")}`}
            />
          )}
          {content.divergences.length === 0 && (
            <EditButton
              onClick={() => openEditor("divergences")}
              label={`+ ${t("summary.divergences")}`}
            />
          )}
        </div>
      )}

      {/* Everyone is told this is a reading rather than quotations: a
          synthesis presented as if it were what people wrote would be a quiet
          lie about the whole section.

          How it was drafted is a different question, and the leader is not
          told — the client's decision. The coach is, because they are the one
          checking it against what people actually said, and because they have
          the sources open on the same screen. */}
      <p className="mt-3 text-[12.5px] text-faint">
        {audience === "HR"
          ? t("summary.methodHR")
          : reviewing
            ? t("summary.methodCoach", { count: feedback.writtenCount })
            : t("summary.method", { count: feedback.writtenCount })}
      </p>

      {review}
    </>
  );
};
